import { memo } from "react";
import { useTranslations } from "next-intl";
import {
  IconAlertTriangle,
  IconBan,
  IconCheck,
  IconLoader2,
  IconSettingsBolt,
  IconX,
} from "@tabler/icons-react";
import { type AiMessage } from "@/providers/AiAssistantProvider";

/**
 * One transcript row per tool call: status icon + human label. Shared by the
 * drawer (`chat`) and the in-builder panel (`builder`, smaller glyphs, no
 * leading bolt). Memoized — streaming re-renders the panel per chunk but
 * untouched tool rows keep identity (§1a). Handles all five tool statuses,
 * including the multi-page generation progress line.
 */
export const ToolStepRow = memo(function ToolStepRow({
  message,
  variant = "chat",
}: {
  message: AiMessage;
  variant?: "chat" | "builder";
}) {
  const t = useTranslations("aiAssistant");
  const builder = variant === "builder";
  const status = message.toolStatus ?? "running";
  const label =
    message.toolName && t.has(`toolNames.${message.toolName}`)
      ? t(`toolNames.${message.toolName}`)
      : (message.toolName ?? "");
  const sz = builder ? 12 : 13;
  const icon =
    status === "running" ? (
      <IconLoader2 size={sz} className="animate-spin motion-reduce:animate-none" />
    ) : status === "done" ? (
      <IconCheck size={sz} />
    ) : status === "awaiting_confirmation" ? (
      <IconAlertTriangle size={sz} />
    ) : status === "declined" ? (
      <IconBan size={sz} />
    ) : (
      <IconX size={sz} />
    );
  const text =
    status === "running"
      ? // A multi-page generation reports per-page progress — a silent
        // multi-minute "Running…" row would read as a hang.
        message.toolProgress
        ? t("studioGeneratingPage", {
            page: message.toolProgress.page,
            total: message.toolProgress.total,
            title: message.toolProgress.title,
          })
        : t("toolRunning", { tool: label })
      : status === "done"
        ? t("toolDone", { tool: label })
        : status === "awaiting_confirmation"
          ? t("toolAwaitingConfirmation", { tool: label })
          : status === "declined"
            ? t("toolDeclined", { tool: label })
            : t("toolFailed", { tool: label });
  return (
    <div className="flex justify-start">
      <span
        data-testid={builder ? "ai-builder-tool-row" : "ai-tool-row"}
        className={`inline-flex items-center gap-1.5 rounded-full border outline-quaternary px-2.5 py-1 text-xs font-tertiary ${
          status === "error" ? "font-error" : ""
        }`}
      >
        {!builder && <IconSettingsBolt size={sz} className="shrink-0" />}
        {icon}
        <span className={`truncate ${builder ? "max-w-44" : "max-w-56"}`}>{text}</span>
      </span>
    </div>
  );
});
